import { Injectable } from '@angular/core';
import {isCarOne} from '../utils/utils';
import {InitialDataSectionFieldKey} from '../consts/initial-data-section-field-key.enum';
import {ApplicationStateService} from '../../shared/services/application-state.service';
import {CalculationData} from '../models/calculation-data.model';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {VehicleStateEnum} from '../consts/vehicle-state.enum';
import {VehicleModelConsts} from '../consts/vehicle-model.consts';
import {VehicleQuestionCode} from '../consts/vehicle-question-code.enum';
import {OwnerDataConsts} from '../consts/owner-data.consts';
import {PartyTypeCode} from '../consts/party-type-code.enum';

@Injectable({
  providedIn: 'root'
})
export class FieldsVisibilityService {

  fieldsVisibilityMechanismEnabled: boolean = isCarOne();

  get sessionFromUrlParam(): boolean {
    return this.stateService.state.calculationDataFromSession;
  }

  get calculationData(): CalculationData {
    return this.stateService.state.sessionCalculationData;
  }

  get vehicleModelChipVisible(): boolean {
    return this.fieldsVisibilityMechanismEnabled ? !this.sessionFromUrlParam : true;
  }

  constructor(private stateService: ApplicationStateService) { }

  isFieldVisible(sectionKey: string, fieldKey: string): boolean {
    if (this.fieldsVisibilityMechanismEnabled && this.sessionFromUrlParam) {
      switch (sectionKey) {
        case 'INITIAL_DATA_SECTION':
          return this.getInitialDataSectionFieldVisibility(fieldKey as InitialDataSectionFieldKey);
        case 'VEHICLE_USE_SECTION':
          return this.getVehicleUseSectionFieldVisibility(fieldKey);
        case 'OWNER_DATA_SECTION':
          return this.getOwnerDataSectionFieldVisibility(fieldKey);
        case 'CONTACT_SECTION':
          return this.getContactDataSectionFieldVisibility(fieldKey);
        default:
          return true;
      }
    }

    return true;
  }

  private getInitialDataSectionFieldVisibility(fieldKey: InitialDataSectionFieldKey): boolean {
    switch (fieldKey) {
      case InitialDataSectionFieldKey.REGISTRATION_NUMBER:
        return !isNotNullOrUndefined(this.calculationData.vehicle) || this.isValueNullOrEmpty(this.calculationData.vehicle.registrationNumber);
      case InitialDataSectionFieldKey.BIRTH_DATE:
        return !isNotNullOrUndefined(this.calculationData.owner) || this.isValueNullOrEmpty(this.calculationData.owner.birthDate);
      default:
        return true;
    }
  }

  private getVehicleUseSectionFieldVisibility(fieldKey: string): boolean {
    switch (fieldKey) {
      case VehicleModelConsts.FIRST_REGISTRATION_DATE:
        return !isNotNullOrUndefined(this.calculationData.vehicle) || this.isValueNullOrEmpty(this.calculationData.vehicle.firstRegistrationDate);
      case VehicleStateEnum.IS_LEASING:
        return !isNotNullOrUndefined(this.calculationData.vehicleState) || this.isValueNullOrEmpty(this.calculationData.vehicleState.questions.find(question => question.questionCode === VehicleQuestionCode.isLeasing));
      default:
        return true;
    }
  }

  private getOwnerDataSectionFieldVisibility(fieldKey: OwnerDataConsts): boolean {
    switch (fieldKey) {
      case OwnerDataConsts.PARTY_CODE_TYPE:
        return false;
      case OwnerDataConsts.DRIVING_LICENSE_ISSUE_YEAR:
        return !isNotNullOrUndefined(this.calculationData.owner) || this.isValueNullOrEmpty(this.calculationData.owner.drivingLicenseIssueYear);
      case OwnerDataConsts.DRIVING_LICENSE_ISSUE_MONTH:
        return !isNotNullOrUndefined(this.calculationData.owner) || this.isValueNullOrEmpty(this.calculationData.owner.drivingLicenseIssueMonth);
      case OwnerDataConsts.COMPANY_NAME:
        return !isNotNullOrUndefined(this.calculationData.owner) || this.isValueNullOrEmpty(this.calculationData.owner.companyName) && this.calculationData.owner.partyTypeCode === PartyTypeCode.BUSINESS_ACTIVITY;
      case OwnerDataConsts.NIP:
        return !isNotNullOrUndefined(this.calculationData.owner) || this.isValueNullOrEmpty(this.calculationData.owner.nip) && this.calculationData.owner.partyTypeCode === PartyTypeCode.BUSINESS_ACTIVITY;
      default:
        return true;
    }
  }

  private getContactDataSectionFieldVisibility(fieldKey: OwnerDataConsts): boolean {
    switch (fieldKey) {
      case OwnerDataConsts.PHONE_NUMBER:
        return false;
      default:
        return true;
    }
  }

  private isValueNullOrEmpty(value: any): boolean {
    return !isNotNullOrUndefined(value) || value === '';
  }
}
